import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GuardianSidebar from '../components/GuardianSidebar';
import Toast, { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import translations from '../i18n/translations';
import DatePicker from '../components/DatePicker';
import { guardianFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const NOTIF_DEFS = [
  {
    key: 'missed_session',
    label: { ar: 'جلسة مفقودة',               en: 'Missed Session'          },
    desc:  { ar: 'يُرسل عند غياب الطالب يومين متتاليين', en: 'Sent when student misses two consecutive days' },
  },
  {
    key: 'streak_break',
    label: { ar: 'السلسلة على وشك الانقطاع',  en: 'Streak About to Break'   },
    desc:  { ar: 'تذكير يومي إذا لم يتدرب الطالب',      en: 'Daily reminder if student has not practiced'  },
  },
  {
    key: 'repeated_weakness',
    label: { ar: 'ضعف متكرر',                 en: 'Repeated Weakness'       },
    desc:  { ar: 'عند تكرار نفس الضعف مرتين', en: 'When the same weakness repeats twice'          },
  },
  {
    key: 'unit_completed',
    label: { ar: 'إتمام وحدة',                en: 'Unit Completed'          },
    desc:  { ar: 'عند إنهاء الطالب وحدة دراسية', en: 'When student finishes a study unit'           },
  },
  {
    key: 'exam_reminder',
    label: { ar: 'امتحان قادم',               en: 'Upcoming Exam'           },
    desc:  { ar: 'قبل 7 أيام من الامتحان',    en: '7 days before an exam'                        },
  },
  {
    key: 'level_upgrade',
    label: { ar: 'ترقية مستوى',              en: 'Level Upgrade'            },
    desc:  { ar: 'عند ارتفاع مستوى الطالب',   en: "When student's level increases"                },
  },
];

function Toggle({ value, onChange }) {
  const { T } = useTheme();
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 46, height: 26, borderRadius: 13,
        background: value ? T.accent : (T.bg === '#f0f4f8' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.1)'),
        position: 'relative', cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.3s ease',
      }}
    >
      <div style={{
        position: 'absolute', top: 3,
        left: value ? 3 : 23,
        width: 20, height: 20, borderRadius: '50%',
        background: 'white',
        transition: 'left 0.3s ease',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function FieldInput({ label, note, value, onChange, type = 'text', error, placeholder }) {
  const { T } = useTheme();
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 7, display: 'block', fontWeight: 500 }}>
        {label}
      </label>
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        dir="auto"
        style={{
          width: '100%', padding: '10px 14px',
          background: T.bgInput, border: `1px solid ${error ? T.error : T.borderInput}`,
          borderRadius: 10, color: T.textPrimary, fontSize: 14,
          boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
          transition: 'border-color 0.2s ease',
        }}
      />
      {note  && <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 5 }}>{note}</div>}
      {error && <div style={{ fontSize: 12, color: T.error, marginTop: 5 }}>{error}</div>}
    </div>
  );
}

function EditStudentModal({ student, onClose, onSaved, showToast }) {
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];

  const [form, setForm] = useState({
    first_name:         student.first_name,
    grade:              String(student.grade),
    school_name:        student.school_name || '',
    ict_or_cs:          student.ict_or_cs || '',
    islamic_lang:       student.islamic_lang || '',
    arabic_lang:        student.arabic_lang || '',
    qatar_history_lang: student.qatar_history_lang || '',
    midterm_exam_date:  student.midterm_exam_date || '',
    final_exam_date:    student.final_exam_date   || '',
  });
  const [monthlyDates, setMonthlyDates] = useState(
    (student.monthly_exam_dates || []).map(d => (typeof d === 'string' ? d : d.date || ''))
  );
  const [dateErrors, setDateErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateDates = () => {
    const errs = {};
    if (!form.midterm_exam_date) errs.midterm = tr.required;
    if (!form.final_exam_date)   errs.final   = tr.required;
    if (form.midterm_exam_date && form.final_exam_date && form.final_exam_date <= form.midterm_exam_date) {
      errs.final = tr.dateOrderError;
    }
    const monthly = {};
    monthlyDates.forEach((d, i) => {
      if (d && form.midterm_exam_date && form.final_exam_date) {
        if (d <= form.midterm_exam_date || d >= form.final_exam_date) {
          monthly[i] = tr.monthlyDateRangeError;
        }
      }
    });
    if (Object.keys(monthly).length) errs.monthly = monthly;
    return errs;
  };

  const grade   = parseInt(form.grade, 10);
  const isQatar = (student.country || '').toLowerCase().includes('q');
  const set     = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const LABELS = {
    firstName:        { ar: 'الاسم الأول',              en: 'First Name'                },
    gradeLabel:       { ar: 'الصف الدراسي',             en: 'Grade'                     },
    gradeOption:      { ar: 'الصف',                     en: 'Grade'                     },
    schoolName:       { ar: 'اسم المدرسة (اختياري)',    en: 'School Name (optional)'    },
    schoolPlaceholder:{ ar: 'اسم المدرسة',              en: 'School name'               },
    ictLabel:         { ar: 'يدرس ICT أو علوم الحاسوب', en: 'Studies ICT or CS'         },
    islamicLang:      { ar: 'لغة التربية الإسلامية',    en: 'Islamic Studies Language'  },
    arabicLang:       { ar: 'لغة اللغة العربية',        en: 'Arabic Language'           },
    qatarHistoryLang: { ar: 'لغة تاريخ قطر',           en: 'Qatar History Language'    },
    chooseOption:     { ar: '— اختر —',                 en: '— Choose —'                },
    arabic:           { ar: 'عربي',                     en: 'Arabic'                    },
    english:          { ar: 'إنجليزي',                  en: 'English'                   },
  };
  const L = (k) => LABELS[k][lang];

  const handleSave = async () => {
    const errs = validateDates();
    if (Object.keys(errs).length) { setDateErrors(errs); return; }
    setLoading(true); setDateErrors({});
    try {
      const res  = await guardianFetch(`${API}/guardian/update-student`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id:          student.id,
          first_name:          form.first_name.trim(),
          grade,
          school_name:         form.school_name.trim() || null,
          ict_or_cs:           (grade >= 7 && grade <= 9) ? (form.ict_or_cs || null) : null,
          islamic_lang:        isQatar ? (form.islamic_lang || null) : null,
          arabic_lang:         isQatar ? (form.arabic_lang || null) : null,
          qatar_history_lang:  isQatar ? (form.qatar_history_lang || null) : null,
          midterm_exam_date:   form.midterm_exam_date || null,
          final_exam_date:     form.final_exam_date   || null,
          monthly_exam_dates:  monthlyDates.filter(d => d),
        }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        showToast(lang === 'ar' ? 'تم تحديث بيانات الطالب ✓' : 'Student updated ✓');
        onSaved();
      } else {
        showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: T.bgInput, border: `1px solid ${T.borderInput}`,
    borderRadius: 10, color: T.textPrimary, fontSize: 14,
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', marginBottom: 14,
  };
  const labelStyle = { fontSize: 13, color: T.textSecondary, marginBottom: 7, display: 'block', fontWeight: 500 };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 18, padding: 32, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: T.textPrimary, marginBottom: 22 }}>
          {lang === 'ar' ? 'تعديل بيانات الطالب' : 'Edit Student'}
        </div>

        <label style={labelStyle}>{L('firstName')}</label>
        <input style={inputStyle} dir="auto" value={form.first_name} onChange={e => set('first_name', e.target.value)} />

        <label style={labelStyle}>{L('gradeLabel')}</label>
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.grade} onChange={e => set('grade', e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(g => (
            <option key={g} value={g}>{L('gradeOption')} {g}</option>
          ))}
        </select>

        <label style={labelStyle}>{L('schoolName')}</label>
        <input style={inputStyle} dir="auto" value={form.school_name}
          onChange={e => set('school_name', e.target.value)}
          placeholder={L('schoolPlaceholder')} />

        {grade >= 7 && grade <= 9 && (
          <>
            <label style={labelStyle}>{L('ictLabel')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.ict_or_cs} onChange={e => set('ict_or_cs', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'لا ينطبق' : 'Not applicable'} —</option>
              <option value="ict">ICT</option>
              <option value="cs">{lang === 'ar' ? 'علوم الحاسوب' : 'Computer Science'}</option>
            </select>
          </>
        )}

        {isQatar && (
          <>
            <label style={labelStyle}>{L('islamicLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.islamic_lang} onChange={e => set('islamic_lang', e.target.value)}>
              <option value="">{L('chooseOption')}</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>

            <label style={labelStyle}>{L('arabicLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.arabic_lang} onChange={e => set('arabic_lang', e.target.value)}>
              <option value="">{L('chooseOption')}</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>

            <label style={labelStyle}>{L('qatarHistoryLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.qatar_history_lang} onChange={e => set('qatar_history_lang', e.target.value)}>
              <option value="">{L('chooseOption')}</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>
          </>
        )}

        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 20, marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.accent, marginBottom: 16 }}>{tr.examDates}</div>

          <label style={labelStyle}>
            {tr.midterm} <span style={{ color: T.error }}>*</span>
          </label>
          <DatePicker
            value={form.midterm_exam_date}
            onChange={v => set('midterm_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.midterm && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.midterm}</div>}

          <label style={labelStyle}>
            {tr.finalExam} <span style={{ color: T.error }}>*</span>
          </label>
          <DatePicker
            value={form.final_exam_date}
            onChange={v => set('final_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.final && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.final}</div>}

          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 10, fontWeight: 500 }}>{tr.monthlyTests}</div>
          {monthlyDates.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={d}
                  onChange={v => {
                    const next = [...monthlyDates];
                    next[i] = v;
                    setMonthlyDates(next);
                  }}
                  placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
                  wrapperStyle={{ marginBottom: 0 }}
                />
                {dateErrors.monthly?.[i] && (
                  <div style={{ fontSize: 12, color: T.error, marginTop: 4 }}>{dateErrors.monthly[i]}</div>
                )}
              </div>
              <button
                onClick={() => setMonthlyDates(monthlyDates.filter((_, j) => j !== i))}
                style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${T.error}`, color: T.error, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', flexShrink: 0 }}
              >✕</button>
            </div>
          ))}
          {monthlyDates.length < 12 && (
            <button
              onClick={() => setMonthlyDates([...monthlyDates, ''])}
              style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, color: T.accent, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}
            >{tr.addDate}</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            style={{ flex: 1, padding: '11px 0', background: T.accent, color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={handleSave} disabled={loading}
          >
            {loading ? '...' : tr.save}
          </button>
          <button
            style={{ flex: 1, padding: '11px 0', background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={onClose}
          >
            {tr.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ student, onClose, onDeleted, showToast }) {
  const { T } = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res  = await guardianFetch(`${API}/guardian/delete-student/${student.id}`, { method: 'DELETE' });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        showToast(lang === 'ar' ? 'تم حذف الطالب ✓' : 'Student deleted ✓');
        onDeleted();
      } else {
        showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
        onClose();
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 18, padding: 32, width: '100%', maxWidth: 420 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: T.textPrimary, marginBottom: 22 }}>
          {tr.confirmDelete}
        </div>
        <div style={{ fontSize: 15, color: T.textPrimary, lineHeight: 1.6, marginBottom: 20 }}>
          {lang === 'ar'
            ? <>هل أنت متأكد من حذف بيانات <strong>{student.first_name}</strong>؟ لن يمكن التراجع عن هذا الإجراء.</>
            : <>Are you sure you want to delete <strong>{student.first_name}</strong>? This action cannot be undone.</>
          }
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            style={{ flex: 1, padding: '11px 0', background: T.error, color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={handleDelete} disabled={loading}
          >
            {loading ? '...' : tr.delete}
          </button>
          <button
            style={{ flex: 1, padding: '11px 0', background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={onClose}
          >
            {tr.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  const { T } = useTheme();
  const skelBg = T.bg === '#f0f4f8' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  return (
    <>
      <style>{`@keyframes gs-pulse{0%,100%{opacity:.4}50%{opacity:.8}}`}</style>
      {[280, 320, 200, 240].map((h, i) => (
        <div key={i} style={{ height: h, borderRadius: 16, marginBottom: 24, background: skelBg, animation: 'gs-pulse 1.6s ease-in-out infinite' }} />
      ))}
    </>
  );
}

export default function GuardianSettings() {
  const navigate = useNavigate();
  const { T, themeMode, setThemeMode } = useTheme();
  const { lang, setLang }             = useGuardianLanguage();
  const tr       = translations[lang];
  const dir      = lang === 'ar' ? 'rtl' : 'ltr';

  const guardianId = localStorage.getItem('jahbath_guardian_id');
  const token      = localStorage.getItem('jahbath_token');

  const { toast, show: showToast } = useToast();

  const [loading,       setLoading]       = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(themeMode);
  const [selectedLang,  setSelectedLang]  = useState(lang);
  const [guardian,      setGuardian]      = useState({ first_name: '', email: '', second_email: '', language_preference: 'ar' });
  const [notifs,   setNotifs]   = useState({
    missed_session: true, streak_break: true, repeated_weakness: true,
    unit_completed: true, exam_reminder: true, level_upgrade: true,
  });
  const [students, setStudents] = useState([]);

  const [accountForm,   setAccountForm]   = useState(null);
  const [accountSaving, setAccountSaving] = useState(false);
  const [notifSaving,   setNotifSaving]   = useState(false);

  const [editStudent,   setEditStudent]   = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);

  const [pwForm,   setPwForm]   = useState({ current: '', newPwd: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (!token || !guardianId) { navigate('/guardian/auth', { replace: true }); return; }
    loadSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await guardianFetch(`${API}/guardian/settings/${guardianId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res) return;
      const json = await res.json();
      if (json.success) {
        const g = json.guardian;
        setGuardian(g);
        setAccountForm({ ...g });
        setNotifs(json.notifications);
        setStudents(json.students || []);
      }
    } catch {}
    finally { setLoading(false); }
  }, [guardianId, token]);

  const handleSaveAccount = async () => {
    setAccountSaving(true);
    try {
      const res  = await guardianFetch(`${API}/guardian/update-profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guardian_id: guardianId, ...accountForm, ui_settings: { theme: selectedTheme, lang: selectedLang } }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setGuardian({ ...accountForm });
        localStorage.setItem('jahbath_report_lang', accountForm.language_preference || 'ar');
        showToast(lang === 'ar' ? 'تم حفظ التغييرات ✓' : 'Changes saved ✓');
      } else {
        showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    } finally {
      setAccountSaving(false);
    }
  };

  const handleSaveNotifs = async () => {
    setNotifSaving(true);
    try {
      const res  = await guardianFetch(`${API}/guardian/update-notifications`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guardian_id: guardianId, notifications: notifs }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        showToast(lang === 'ar' ? 'تم حفظ الإعدادات ✓' : 'Settings saved ✓');
      } else {
        showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const errs = {};
    if (pwForm.newPwd.length < 8) errs.newPwd = lang === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters';
    if (pwForm.newPwd !== pwForm.confirm) errs.confirm = lang === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match';
    if (Object.keys(errs).length) { setPwErrors(errs); return; }

    setPwSaving(true); setPwErrors({});
    try {
      const res  = await guardianFetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ guardian_id: guardianId, current_password: pwForm.current, new_password: pwForm.newPwd }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setPwForm({ current: '', newPwd: '', confirm: '' });
        showToast(lang === 'ar' ? 'تم تغيير كلمة المرور بنجاح ✓' : 'Password changed successfully ✓');
      } else {
        const code = data.errors?.[0]?.code || '';
        if (code === 'JAH_ERR_INVALID_CREDENTIALS') {
          setPwErrors({ current: lang === 'ar' ? 'كلمة المرور الحالية غير صحيحة' : 'Current password is incorrect' });
        } else {
          showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
        }
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    } finally {
      setPwSaving(false);
    }
  };

  const handleLogout = async () => {
    await logoutAndClearLocal('guardian');
    navigate('/guardian/auth', { replace: true });
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  if (!token || !guardianId) return null;

  const sectionStyle = {
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
    border: `1px solid ${T.border}`,
    borderRadius: 18, padding: '28px 32px', marginBottom: 24,
    boxShadow: '0 4px 20px rgba(5,150,105,0.10)',
  };
  const sectionTitle = { fontSize: 17, fontWeight: 700, color: T.textPrimary, marginBottom: 4 };
  const sectionSub   = { fontSize: 13, color: T.textSecondary, marginBottom: 22 };
  const saveBtn = {
    marginTop: 8, padding: '11px 28px',
    background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white',
    border: 'none', borderRadius: 12,
    boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    transition: 'opacity 0.2s ease', fontFamily: 'inherit',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent', color: T.textPrimary, fontFamily: "'Sora', sans-serif", direction: dir, transition: 'background 0.2s ease, color 0.2s ease' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&display=swap');
        @media (max-width: 767px) {
          main { width: 100% !important; padding: 12px !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important; }
          main [style*="font-size: 26px"] { font-size: 18px !important; }
          [style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
          [style*="inset: 0"] > div { width: 95vw !important; max-width: 95vw !important; padding: 16px !important; margin: 10px auto !important; box-sizing: border-box !important; }
          table { display: block !important; overflow-x: auto !important; }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          main { padding: 20px !important; }
          [style*="grid-template-columns"] { grid-template-columns: repeat(2, 1fr) !important; }
          [style*="inset: 0"] > div { max-width: 680px !important; }
        }
      `}</style>
      <GuardianSidebar
        guardianName={guardian.first_name}
        guardianEmail={guardian.email}
        onLogout={handleLogout}
      />

      <main style={{ flex: 1, padding: '32px 36px', overflowY: 'auto', maxWidth: 900, position: 'relative', zIndex: 10 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>{tr.settings}</div>
        <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 32 }}>
          {lang === 'ar' ? 'إدارة حسابك وتفضيلاتك' : 'Manage your account and preferences'}
        </div>

        {loading ? <LoadingSkeleton /> : (
          <>
            {/* Appearance */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>{lang === 'ar' ? 'المظهر واللغة' : 'Appearance & Language'}</div>
              <div style={sectionSub}>{lang === 'ar' ? 'التغييرات تُطبق فوراً — احفظ للاحتفاظ بها' : 'Changes apply instantly — save to keep them'}</div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8, display: 'block', fontWeight: 500 }}>
                  {lang === 'ar' ? 'المظهر' : 'Theme'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { val: 'system', labelAr: 'تلقائي', labelEn: 'Auto', icon: '💻' },
                    { val: 'light',  labelAr: 'فاتح',   labelEn: 'Light', icon: '☀️' },
                    { val: 'dark',   labelAr: 'داكن',   labelEn: 'Dark',  icon: '🌙' },
                  ].map(opt => (
                    <button key={opt.val}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 10,
                        border: `1px solid ${selectedTheme === opt.val ? T.accent : T.border}`,
                        background: selectedTheme === opt.val ? `${T.accent}20` : 'transparent',
                        color: selectedTheme === opt.val ? T.accent : T.textSecondary,
                        fontSize: 14, fontWeight: selectedTheme === opt.val ? 600 : 400,
                        cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit',
                      }}
                      onClick={() => { setSelectedTheme(opt.val); setThemeMode(opt.val); }}
                    >
                      {opt.icon} {lang === 'ar' ? opt.labelAr : opt.labelEn}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8, display: 'block', fontWeight: 500 }}>
                  {lang === 'ar' ? 'لغة الواجهة' : 'Interface Language'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { val: 'ar', label: 'العربية' },
                    { val: 'en', label: 'English'  },
                  ].map(opt => (
                    <button key={opt.val}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 10,
                        border: `1px solid ${selectedLang === opt.val ? T.accent : T.border}`,
                        background: selectedLang === opt.val ? `${T.accent}20` : 'transparent',
                        color: selectedLang === opt.val ? T.accent : T.textSecondary,
                        fontSize: 14, fontWeight: selectedLang === opt.val ? 600 : 400,
                        cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit',
                      }}
                      onClick={() => { setSelectedLang(opt.val); setLang(opt.val); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Account Settings */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>{tr.accountSettings}</div>
              <div style={sectionSub}>{lang === 'ar' ? 'بيانات حسابك الشخصية' : 'Your personal account information'}</div>

              {accountForm && (
                <>
                  <FieldInput
                    label={lang === 'ar' ? 'الاسم الأول' : 'First Name'}
                    value={accountForm.first_name}
                    onChange={v => setAccountForm(f => ({ ...f, first_name: v }))}
                  />
                  <FieldInput
                    label={lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    type="email"
                    value={accountForm.email}
                    onChange={v => setAccountForm(f => ({ ...f, email: v }))}
                    note={lang === 'ar' ? 'سيتم إرسال التقارير على هذا البريد' : 'Reports will be sent to this email'}
                  />
                  <FieldInput
                    label={lang === 'ar' ? 'بريد إلكتروني ثانٍ (اختياري)' : 'Second Email (optional)'}
                    type="email"
                    value={accountForm.second_email || ''}
                    onChange={v => setAccountForm(f => ({ ...f, second_email: v }))}
                    placeholder={lang === 'ar' ? 'للمدرسة أو ولي أمر آخر' : 'For school or another guardian'}
                  />

                  <div style={{ marginBottom: 18 }}>
                    <label style={{ fontSize: 13, color: T.textSecondary, marginBottom: 7, display: 'block', fontWeight: 500 }}>
                      {lang === 'ar' ? 'لغة التقارير' : 'Report Language'}
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { val: 'ar', labelAr: 'العربية',  labelEn: 'Arabic'  },
                        { val: 'en', labelAr: 'English',  labelEn: 'English' },
                      ].map(opt => (
                        <button key={opt.val}
                          style={{
                            flex: 1, padding: '9px 0', borderRadius: 10,
                            border: `1px solid ${accountForm.language_preference === opt.val ? T.accent : T.border}`,
                            background: accountForm.language_preference === opt.val ? `${T.accent}25` : 'transparent',
                            color: accountForm.language_preference === opt.val ? T.accent : T.textSecondary,
                            fontSize: 14, fontWeight: accountForm.language_preference === opt.val ? 600 : 400,
                            cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit',
                          }}
                          onClick={() => setAccountForm(f => ({ ...f, language_preference: opt.val }))}
                        >
                          {lang === 'ar' ? opt.labelAr : opt.labelEn}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button style={saveBtn} onClick={handleSaveAccount} disabled={accountSaving}>
                    {accountSaving ? '...' : tr.save}
                  </button>
                </>
              )}
            </div>

            {/* Notification Settings */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>{tr.notifSettings}</div>
              <div style={sectionSub}>
                {lang === 'ar' ? 'اختر الإشعارات التي تريد استلامها' : 'Choose which notifications to receive'}
              </div>

              {NOTIF_DEFS.map((n, i) => (
                <div key={n.key} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 0',
                  borderBottom: i < NOTIF_DEFS.length - 1 ? `1px solid ${T.border}` : 'none',
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{n.label[lang]}</div>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 3 }}>{n.desc[lang]}</div>
                  </div>
                  <Toggle value={notifs[n.key]} onChange={v => setNotifs(prev => ({ ...prev, [n.key]: v }))} />
                </div>
              ))}

              <button style={{ ...saveBtn, marginTop: 20 }} onClick={handleSaveNotifs} disabled={notifSaving}>
                {notifSaving ? '...' : tr.saveNotifs}
              </button>
            </div>

            {/* Manage Students */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>{tr.manageStudents}</div>
              <div style={sectionSub}>
                {lang === 'ar' ? 'تعديل أو حذف ملفات الطلاب' : 'Edit or delete student profiles'}
              </div>

              {students.length === 0 ? (
                <div style={{ color: T.textSecondary, fontSize: 14 }}>
                  {lang === 'ar' ? 'لا يوجد طلاب مسجلون' : 'No students registered'}
                </div>
              ) : (
                students.map((s, i) => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 0',
                    borderBottom: i < students.length - 1 ? `1px solid ${T.border}` : 'none',
                  }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary }}>{s.first_name}</div>
                      <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 2 }}>
                        {lang === 'ar' ? 'الصف' : 'Grade'} {s.grade} · {s.country}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        style={{ padding: '7px 16px', background: 'transparent', border: `1px solid ${T.border}`, color: T.textPrimary, borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                        onClick={() => setEditStudent(s)}
                      >
                        {tr.edit}
                      </button>
                      <button
                        style={{ padding: '7px 16px', background: 'transparent', border: `1px solid ${T.error}`, color: T.error, borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                        onClick={() => setDeleteStudent(s)}
                      >
                        {tr.delete}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Change Password */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>{tr.changePassword}</div>
              <div style={sectionSub}>
                {lang === 'ar' ? 'اختر كلمة مرور قوية لحماية حسابك' : 'Choose a strong password to protect your account'}
              </div>

              <FieldInput
                label={lang === 'ar' ? 'كلمة المرور الحالية' : 'Current Password'}
                type="password"
                value={pwForm.current}
                onChange={v => setPwForm(f => ({ ...f, current: v }))}
                error={pwErrors.current}
              />
              <FieldInput
                label={lang === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                type="password"
                value={pwForm.newPwd}
                onChange={v => setPwForm(f => ({ ...f, newPwd: v }))}
                error={pwErrors.newPwd}
              />
              <FieldInput
                label={lang === 'ar' ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password'}
                type="password"
                value={pwForm.confirm}
                onChange={v => setPwForm(f => ({ ...f, confirm: v }))}
                error={pwErrors.confirm}
              />

              <button style={saveBtn} onClick={handleChangePassword} disabled={pwSaving}>
                {pwSaving ? '...' : tr.changePassword}
              </button>
            </div>
          </>
        )}
      </main>

      {editStudent && (
        <EditStudentModal
          student={editStudent}
          onClose={() => setEditStudent(null)}
          onSaved={() => { setEditStudent(null); loadSettings(); }}
          showToast={showToast}
        />
      )}

      {deleteStudent && (
        <DeleteConfirmModal
          student={deleteStudent}
          onClose={() => setDeleteStudent(null)}
          onDeleted={() => {
            setStudents(prev => prev.filter(s => s.id !== deleteStudent.id));
            setDeleteStudent(null);
          }}
          showToast={showToast}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}
